import { AuthProvider } from "@refinedev/core";
import { AxiosInstance } from "axios";

export const TOKEN_KEY = "emr-auth";
export const ROLE_KEY = "emr-role";
export const USER_KEY = "emr-user";
export const AVATAR_ICON_KEY = "emr-avatar-icon";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
const SIGN_IN_URL = `${API_URL}/auth/signin`;

// Block protocol-relative URLs (//evil.com) and backslash variants (/\evil.com)
// which browsers treat as external hosts. Decode percent-encoding first to
// catch encoded bypasses like /%2F%2Fevil.com.
export const isSafeRedirect = (path: string | null | undefined): path is string => {
  if (typeof path !== "string" || !path.startsWith("/")) return false;
  try {
    const decoded = decodeURIComponent(path);
    return !decoded.startsWith("//") && !decoded.startsWith("/\\");
  } catch {
    return false;
  }
};

export const authProvider = (axiosInstance: AxiosInstance): AuthProvider => {
  return {
    login: async ({ username, password, redirectTo }: { username: string; password: string; redirectTo?: string }) => {
      try {
        const { data } = await axiosInstance.post(`${SIGN_IN_URL}`, {
          username,
          password,
        });

        const { role, firstName, lastName, email } = data;

        if (data && data?.accessToken) {
          localStorage.setItem(TOKEN_KEY, data?.accessToken);
          localStorage.setItem(ROLE_KEY, role);
          localStorage.setItem(USER_KEY, `${firstName} ${lastName}`);
          localStorage.setItem('emr-email', email || username || '');
          localStorage.setItem(AVATAR_ICON_KEY, String(Math.floor(Math.random() * 8)));
          return {
            success: true,
            redirectTo: isSafeRedirect(redirectTo) ? redirectTo : "/dashboard",
          };
        }

        return {
          success: false,
          error: {
            name: "Login failed",
            message: "Invalid response from server",
          },
        };
      } catch (error) {
        return {
          success: false,
          error: {
            name: "Login failed",
            message: "Invalid username or password",
          },
        };
      }
    },
    logout: async () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('emr-email');
      localStorage.removeItem(AVATAR_ICON_KEY);
      return {
        success: true,
        redirectTo: "/login",
      };
    },
    check: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        return {
          authenticated: true,
        };
      }
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    },
    onError: async (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ROLE_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('emr-email');
        localStorage.removeItem(AVATAR_ICON_KEY);
        return {
          logout: true,
          redirectTo: "/login",
          error: {
            name: "Unauthorized",
            message: "Please sign in to your account",
          },
        };
      }
      return {
        error,
      };
    },
    getPermissions: async () => {
      return null;
    },
    getIdentity: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        return null;
      }
      return {
        id: 1,
        name: localStorage.getItem(USER_KEY),
        email: localStorage.getItem('emr-email'),
        avatarIconIndex: parseInt(localStorage.getItem(AVATAR_ICON_KEY) || '0', 10),
        role: localStorage.getItem(ROLE_KEY),
      };
    },
  };
};
